import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Board, { Piece } from '../components/Board';
import { useTitle } from '../components/Layout';
import { movesFrom } from '../chess';
import { profile } from '../data';

// Every white piece on the home board is a door into the portfolio.
const DOORS = [
  { sq: 'e3', piece: 'K', to: '/opening', name: 'The King', section: 'Opening', copy: 'Who I am, what I play, where I trained.' },
  { sq: 'b6', piece: 'R', to: '/moves', name: 'The Rook', section: 'Moves', copy: 'My career, replayed one move at a time.' },
  { sq: 'd5', piece: 'Q', to: '/puzzles', name: 'The Queen', section: 'Puzzles', copy: 'Eight real business problems. Solve them on the board.' },
  { sq: 'f4', piece: 'N', to: '/endgame', name: 'The Knight', section: 'Endgame', copy: 'Your move. Résumé, email, LinkedIn, or a call.' },
  { sq: 'g6', piece: 'B', to: '/classic', name: 'The Bishop', section: 'Classic', copy: 'Short on time? The diagonal shortcut to a plain résumé.' },
];

const POSITION = DOORS.reduce((pos, d) => ({ ...pos, [d.sq]: d.piece }), { h8: 'k' });

export default function Home() {
  useTitle(null);
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const active = DOORS.find((d) => d.sq === hovered);
  const targets = useMemo(() => (hovered ? movesFrom(POSITION, hovered) : []), [hovered]);
  const labels = Object.fromEntries(DOORS.map((d) => [d.sq, `${d.name}: ${d.section}. ${d.copy}`]));

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">Portfolio · Interactive edition</p>
          <h1 className="display">
            Rohan Rout<span className="display-accent">.</span>
          </h1>
          <p className="hero-role">
            {profile.role} <span className="muted">· {profile.focus}</span>
          </p>
          <p className="hero-lede">{profile.tagline}.</p>
          <p className="hero-body">{profile.intro}</p>
          <div className="cta-row">
            <Link to="/opening" className="btn btn-primary">
              Start the game <span aria-hidden="true">→</span>
            </Link>
            <Link to="/classic" className="btn btn-ghost">
              Classic résumé
            </Link>
          </div>
        </div>

        <div className="hero-board">
          <Board
            position={POSITION}
            hovered={hovered}
            targets={targets}
            labels={labels}
            isClickable={(sq) => Boolean(DOORS.find((d) => d.sq === sq))}
            onSquareClick={(sq) => navigate(DOORS.find((d) => d.sq === sq).to)}
            onSquareHover={setHovered}
            ariaLabel="Navigation board. Each white piece opens a section."
            className="board-home"
          />
          <p className="board-caption" aria-live="polite">
            {active ? (
              <>
                <strong>{active.name}</strong> → {active.section}. {active.copy}
              </>
            ) : (
              <>Every white piece is a door. Pick one.</>
            )}
          </p>
        </div>
      </section>

      <section className="legend" aria-label="How to navigate">
        {DOORS.map((d) => (
          <Link
            key={d.sq}
            to={d.to}
            className={`legend-item ${hovered === d.sq ? 'is-active' : ''}`}
            onMouseEnter={() => setHovered(d.sq)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(d.sq)}
            onBlur={() => setHovered(null)}
          >
            <span className="legend-piece">
              <Piece piece={d.piece} />
            </span>
            <span className="legend-text">
              <span className="legend-section">{d.section}</span>
              <span className="legend-copy">{d.copy}</span>
            </span>
          </Link>
        ))}
      </section>
    </div>
  );
}
